import { PRIVATE_STATUSES, QUEUE_ASSIGNEE_FILTERS, REQUEST_STATUSES, type RequestStatus } from "@procuremate/shared";
import { TriangleAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import { SelectField } from "@/components/ui/select-field";
import { TextField } from "@/components/ui/text-field";
import { RequestStatusBadge } from "@/features/requests/request-status-badge";
import { QUEUE_PAGE_SIZE, type QueueParams, useQueue } from "./api";

const SEARCH_DELAY_MS = 300;
const STATUS_FILTERS = ["open", "all", ...REQUEST_STATUSES.filter((s) => !PRIVATE_STATUSES.has(s))] as const;

/**
 * The buyer's queue (WP-3.1, ADR-0022): submitted requests they can see, never their own, oldest
 * submission first. Filters live in the URL (shareable, back-button safe).
 */
export function QueuePage() {
  const { t, i18n } = useTranslation();
  const [params, setParams] = useSearchParams();
  const statusParam = params.get("status") ?? "open";
  const status = (
    (STATUS_FILTERS as readonly string[]).includes(statusParam) ? statusParam : "open"
  ) as QueueParams["status"];
  const assigneeParam = params.get("assignee") ?? "any";
  const assignee = (
    (QUEUE_ASSIGNEE_FILTERS as readonly string[]).includes(assigneeParam) ? assigneeParam : "any"
  ) as QueueParams["assignee"];
  const q = params.get("q") ?? "";
  const page = Math.max(1, Number(params.get("page")) || 1);
  const [search, setSearch] = useState(q);

  // Typing updates the URL after a short pause (one request per pause, not per keystroke).
  useEffect(() => {
    if (search.trim() === q) return;
    const timer = setTimeout(() => update({ q: search.trim(), page: "" }), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  });

  function update(changes: Record<string, string>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setParams(next, { replace: true });
  }

  const queue = useQueue({ status, assignee, q, page });
  const total = queue.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / QUEUE_PAGE_SIZE));
  const dateFormat = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" });
  const statusLabel = (s: (typeof STATUS_FILTERS)[number]) =>
    s === "open" ? t("review.queue.openWork") : s === "all" ? t("review.queue.allStatuses") : t(`review.status.${s}`);

  return (
    <section className="rounded-lg bg-surface p-5 shadow-card sm:p-6">
      <h1 className="mb-1 text-2xl font-bold text-primary">{t("review.queue.title")}</h1>
      <p className="mb-4 text-sm text-fg-muted">{t("review.queue.intro")}</p>

      <div className="mb-4 grid gap-4 sm:grid-cols-[1fr_12rem_12rem]">
        <TextField
          id="queue-search"
          label={t("review.queue.search")}
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <SelectField
          id="queue-status"
          label={t("review.queue.status")}
          value={status}
          options={STATUS_FILTERS.map((s) => ({ value: s, label: statusLabel(s) }))}
          onChange={(event) => update({ status: event.target.value === "open" ? "" : event.target.value, page: "" })}
        />
        <SelectField
          id="queue-assignee"
          label={t("review.queue.assignee")}
          value={assignee}
          options={QUEUE_ASSIGNEE_FILTERS.map((a) => ({ value: a, label: t(`review.queue.assigneeFilter.${a}`) }))}
          onChange={(event) => update({ assignee: event.target.value === "any" ? "" : event.target.value, page: "" })}
        />
      </div>

      {queue.isError ? (
        <p role="alert" className="rounded-sm bg-danger-bg px-3 py-2 text-sm text-danger">
          {t("review.loadError")}
        </p>
      ) : null}

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <caption className="sr-only">{t("review.queue.caption")}</caption>
          <thead className="border-b border-border text-fg-muted">
            <tr>
              <th scope="col" className="py-2 pr-4 font-semibold">
                {t("review.queue.request")}
              </th>
              <th scope="col" className="py-2 pr-4 font-semibold">
                {t("review.queue.status")}
              </th>
              <th scope="col" className="hidden py-2 pr-4 font-semibold md:table-cell">
                {t("review.queue.organisation")}
              </th>
              <th scope="col" className="hidden py-2 pr-4 font-semibold lg:table-cell">
                {t("review.queue.assignee")}
              </th>
              <th scope="col" className="hidden py-2 font-semibold sm:table-cell">
                {t("review.queue.submitted")}
              </th>
            </tr>
          </thead>
          <tbody>
            {queue.data?.items.map((r) => (
              <tr key={r.id} className="border-b border-border last:border-0">
                <td className="py-3 pr-4 align-top">
                  <Link to={`/queue/${r.id}`} className="font-semibold text-link hover:underline">
                    <span className="tabular">{r.reference}</span>
                    {" · "}
                    {r.title ?? t("requests.list.untitled")}
                  </Link>
                  <div className="text-fg-muted">
                    {r.requester.displayName}
                    {r.engagementShape ? (
                      <>
                        {" · "}
                        {t(`requests.panel.shapes.${r.engagementShape}`)}
                        {r.sizeTier ? ` · ${t(`requests.panel.tiers.${r.sizeTier}`)}` : null}
                      </>
                    ) : null}
                  </div>
                  {r.warnings > 0 ? (
                    <div className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-finding">
                      <TriangleAlert aria-hidden="true" className="h-3.5 w-3.5" />
                      {t("review.queue.warnings", { count: r.warnings })}
                    </div>
                  ) : null}
                </td>
                <td className="py-3 pr-4 align-top">
                  <RequestStatusBadge status={r.status as RequestStatus} audience="buyer" />
                  {/* Phones: who has it, under the status. */}
                  <div className="mt-1 text-xs text-fg-muted lg:hidden">
                    {r.assignee ? r.assignee.displayName : t("review.unassigned")}
                  </div>
                </td>
                <td className="hidden py-3 pr-4 align-top md:table-cell">
                  {r.legalEntity || r.businessUnit ? (
                    <>
                      <div>{r.legalEntity?.name ?? "—"}</div>
                      <div className="text-fg-muted">{r.businessUnit?.name ?? "—"}</div>
                    </>
                  ) : (
                    <span className="text-fg-muted">{t("review.unrouted")}</span>
                  )}
                </td>
                <td className="hidden py-3 pr-4 align-top lg:table-cell">
                  {r.assignee ? (
                    r.assignee.displayName
                  ) : (
                    <span className="text-fg-muted">{t("review.unassigned")}</span>
                  )}
                </td>
                <td className="hidden py-3 align-top text-fg-muted sm:table-cell">
                  {r.submittedAt ? dateFormat.format(new Date(r.submittedAt)) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {queue.data && queue.data.items.length === 0 ? (
        <p className="py-6 text-center text-fg-muted">{t("review.queue.empty")}</p>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-fg-muted">
        <p aria-live="polite">{t("review.queue.count", { count: total })}</p>
        {pages > 1 ? (
          <div className="flex items-center gap-2">
            <Button variant="secondary" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>
              {t("admin.users.previous")}
            </Button>
            <span>{t("admin.users.pageOf", { page, pages })}</span>
            <Button variant="secondary" disabled={page >= pages} onClick={() => update({ page: String(page + 1) })}>
              {t("admin.users.next")}
            </Button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
